import React from 'react';
import { useRevenueSummaryQuery } from '@/api';
import type { RevenueSummary } from '@/api/models';
import type { ErrorResponse } from '@/api/handlers/responses';

/**
 * Returns a `RevenueSummary` snapshot for the widget.
 *
 * Wraps `useRevenueSummaryQuery` (TanStack Query under the hood) in the
 * widget's consumer contract so the view + container don't have to learn
 * the query's internals. Mapping rules:
 *
 *   - `isLoading` → `query.isPending` (true only on the first fetch). Use
 *     `query.isFetching` if we ever want skeletons during background
 *     refetches too.
 *   - `data` → the deserialized `RevenueSummary` on success, `null` otherwise.
 *   - `error` → the `ErrorResponse` from the unsuccessful `ApiResponse`, or
 *     `null` on success. Note: the queryFn never rejects (errors are folded
 *     into the response envelope by `makeApiRequest`), so the standard
 *     `query.error` field is unused.
 *   - `onRetry` → `query.refetch` adapted to a `() => void`.
 */
export const useRevenueSummary = () => {
    const query = useRevenueSummaryQuery();

    const data = React.useMemo<RevenueSummary | null>(
        () => (query.data?.success ? query.data.response : null),
        [query.data],
    );

    const error = React.useMemo<ErrorResponse | null>(
        () =>
            query.data && !query.data.success
                ? (query.data.error ?? null)
                : null,
        [query.data],
    );

    const onRetry = React.useCallback(() => {
        void query.refetch();
    }, [query]);

    return {
        data,
        error,
        isLoading: query.isPending,
        onRetry,
    } as const;
};
